import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ProfessionalCard } from "@/components/professionals/ProfessionalCard";
import { Link } from "react-router-dom";
import { MapPin, Star, Clock } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BrandedImage } from "@/components/images/BrandedImage";

interface EmbedConfig {
  id: string;
  professional_type: string;
  city: string | null;
  max_items: number;
}

interface EmbeddedListingsProps {
  articleId: string;
}

const TYPE_LABELS: Record<string, string> = {
  entrenadores: "Entrenadores Personales",
  fisios: "Fisioterapeutas",
  nutris: "Nutricionistas",
  yoga: "Centros de Yoga",
  crossfit: "Centros de CrossFit",
  gimnasios: "Gimnasios",
  pilates: "Centros de Pilates",
  baile: "Escuelas de Baile",
  "artes-marciales": "Artes Marciales",
};

const PROFESSIONAL_TYPES = ["entrenadores", "fisios", "nutris"];

const ListingCard = ({ listing }: { listing: any }) => (
  <Link to={`/listing/${listing.slug || listing.id}`}>
    <Card className="hover:shadow-lg transition-shadow overflow-hidden h-full">
      <CardHeader className="p-0">
        <BrandedImage
          src={listing.image_url || listing.logo_url}
          category="gym"
          alt={listing.name}
          aspectRatio="aspect-video"
          logoSize="md"
          logoPosition="bottom-right"
        />
      </CardHeader>
      <CardContent className="p-4 space-y-2">
        <h3 className="font-bold text-lg line-clamp-1">{listing.name}</h3>
        {listing.city && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="w-3 h-3" />
            <span>{listing.city}</span>
          </div>
        )}
        {listing.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{listing.description}</p>
        )}
        {listing.rating && (
          <div className="flex items-center gap-1 text-sm">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span>{listing.rating}</span>
          </div>
        )}
      </CardContent>
    </Card>
  </Link>
);

export const EmbeddedListings = ({ articleId }: EmbeddedListingsProps) => {
  const [configs, setConfigs] = useState<EmbedConfig[]>([]);
  const [results, setResults] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfigs = async () => {
      const { data } = await supabase
        .from("article_embedded_listings")
        .select("*")
        .eq("article_id", articleId);

      if (data && data.length > 0) {
        setConfigs(data);
        await fetchAllResults(data);
      }
      setLoading(false);
    };

    loadConfigs();
  }, [articleId]);

  const fetchAllResults = async (cfgs: EmbedConfig[]) => {
    const allResults: Record<string, any[]> = {};

    await Promise.all(
      cfgs.map(async (cfg) => {
        const items = await fetchForType(cfg.professional_type, cfg.city, cfg.max_items);
        allResults[cfg.id] = items;
      })
    );

    setResults(allResults);
  };

  const fetchForType = async (type: string, city: string | null, limit: number) => {
    try {
      if (type === "entrenadores") {
        let q = supabase
          .from("trainer_profiles_public")
          .select("*")
          .eq("is_active", true)
          .limit(limit);
        if (city) q = q.ilike("location_city", `%${city}%`);
        const { data } = await q;
        return (data || []).map((t) => ({ ...t, _type: "trainer" }));
      }

      if (type === "fisios") {
        let q = supabase
          .from("physiotherapist_profiles")
          .select("*")
          .eq("is_active", true)
          .limit(limit);
        if (city) q = q.ilike("location_city", `%${city}%`);
        const { data } = await q;
        return (data || []).map((p) => ({ ...p, _type: "physiotherapist" }));
      }

      if (type === "nutris") {
        let q = supabase
          .from("nutritionist_profiles")
          .select("*")
          .eq("is_active", true)
          .limit(limit);
        if (city) q = q.ilike("location_city", `%${city}%`);
        const { data } = await q;
        return (data || []).map((n) => ({ ...n, _type: "nutritionist" }));
      }

      // Listings-based types
      let q = supabase
        .from("listings")
        .select("*")
        .eq("status", "active")
        .limit(limit);

      if (city) q = q.ilike("city", `%${city}%`);

      // Type filters
      switch (type) {
        case "yoga":
          q = q.ilike("type", "%yoga%");
          break;
        case "crossfit":
          q = q.ilike("type", "%crossfit%");
          break;
        case "gimnasios":
          q = q.or("type.ilike.%gimnasio%,type.ilike.%gym%,type.ilike.%fitness%");
          break;
        case "pilates":
          q = q.ilike("type", "%pilates%");
          break;
        case "baile":
          q = q.or("type.ilike.%baile%,type.ilike.%dance%,type.ilike.%escuela de baile%");
          break;
        case "artes-marciales":
          q = q.or("type.ilike.%artes marciales%,type.ilike.%martial%,type.ilike.%boxeo%");
          break;
      }

      const { data } = await q;
      return (data || []).map((l) => ({ ...l, _type: "listing" }));
    } catch (err) {
      console.error("Error fetching embedded listings:", err);
      return [];
    }
  };

  if (loading || configs.length === 0) return null;

  return (
    <div className="space-y-12 my-12">
      {configs.map((cfg) => {
        const items = results[cfg.id] || [];
        if (items.length === 0) return null;

        const label = TYPE_LABELS[cfg.professional_type] || cfg.professional_type;
        const heading = cfg.city ? `${label} en ${cfg.city}` : label;
        const isProfessional = PROFESSIONAL_TYPES.includes(cfg.professional_type);

        return (
          <section key={cfg.id}>
            <h2 className="text-2xl md:text-3xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              {heading}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => {
                if (isProfessional) {
                  const profType = item._type as "trainer" | "nutritionist" | "physiotherapist";
                  return (
                    <ProfessionalCard
                      key={item.id}
                      id={item.id}
                      slug={item.slug}
                      fullName={item.full_name || item.name || ""}
                      locationCity={item.location_city || ""}
                      locationCountry={item.location_country || "España"}
                      specialty={item.specialty || item.specialties || []}
                      yearsExperience={item.years_experience || 0}
                      hourlyRate={item.hourly_rate || 0}
                      bio={item.bio || ""}
                      profileImage={item.profile_image_url || item.profile_image}
                      subscriptionType={item.subscription_type}
                      type={profType}
                      source="profile"
                    />
                  );
                }
                return <ListingCard key={item.id} listing={item} />;
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};
